import { useMemo } from 'react';
import PhotoAlbum from 'react-photo-album';
import {
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { NavLink } from 'react-router-dom';
import { formatDate } from '../../helpers';
import { FindListFilters } from './FindListFilters';
import { searchFieldStyles } from './searchFieldStyles';
import { FindResult, FindResultsState } from './types';
import {
  EMPTY_FIND_FILTER_LIST_KEYS,
  FindFilterListKey,
} from '../FindToolbox/Filters/types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useVocabulary } from '../../i18n';

const getImageSrc = (find: FindResult) => {
  const amcrImage = find.amcrData?.images?.[0];
  if (amcrImage) {
    return `https://digiarchiv.aiscr.cz/api/img/medium?size=medium&id=${amcrImage}`;
  }

  const image = find.images?.[0];
  if (!image) {
    return 'https://acolyte-images.s3.eu-central-1.amazonaws.com/no-image-alpha.png';
  }

  return `https://imagedelivery.net/3zdDZwDRXumMqC2LPbSSXw/${image}/public`;
};

const getFormattedDate = (date?: string | Date | null) =>
  formatDate(date) || '-/-';

const matchesSearch = (
  find: FindResult,
  search: string,
  localizedValues: string[]
) => {
  if (!search) {
    return true;
  }

  const needle = search.toLowerCase();
  const fields = [
    find.id,
    find.evId,
    find.type,
    find.material,
    find.era,
    find.description,
    ...localizedValues,
  ];

  return fields.some(
    (field) => field != null && String(field).toLowerCase().includes(needle)
  );
};

const GALLERY_PAGE_SIZE = 20;
const photoLinkStyles = {
  border: '2px solid',
  borderColor: 'divider',
  borderRadius: '4px',
  textDecoration: 'none',
  color: 'inherit',
  minWidth: 0,
  overflow: 'hidden',
};
const photoImageFrameStyles = { width: '100%', overflow: 'hidden' };
const photoImageStyles = {
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'cover',
} as const;
const photoMetadataStyles = { minWidth: 0, py: 1 };
const photoMetadataRowStyles = { minWidth: 0, gap: 1 };
const leadingMetadataStyles = { minWidth: 0, flexGrow: 1 };
const trailingMetadataStyles = {
  minWidth: 0,
  flexShrink: 0,
  maxWidth: '45%',
  textAlign: 'right',
};
const galleryRowConstraints = { maxPhotos: 4 };

const renderPhoto = ({
  photo,
  imageProps: { alt, style: _imageStyle, ...restImageProps },
  layoutOptions,
  wrapperStyle,
}) => {
  const cardStyle = {
    ...wrapperStyle,
    aspectRatio: 'auto',
    padding: `${layoutOptions.padding - 2}px`,
    paddingBottom: 0,
  };
  const imageFrameStyle = { aspectRatio: `${photo.width} / ${photo.height}` };
  const primaryId = photo.meta.amcrId || photo.meta.id;

  return (
    <Box
      to={`/f/${photo.meta.id}/edit`}
      component={NavLink}
      style={cardStyle}
      sx={photoLinkStyles}
    >
      <Box style={imageFrameStyle} sx={photoImageFrameStyles}>
        <img alt={alt} style={photoImageStyles} {...restImageProps} />
      </Box>
      <Stack sx={photoMetadataStyles}>
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={photoMetadataRowStyles}
        >
          <Typography
            fontWeight="bold"
            fontSize={14}
            noWrap
            sx={leadingMetadataStyles}
            title={photo.meta.type || photo.meta.unidentified}
          >
            {photo.meta.type || photo.meta.unidentified}
          </Typography>
          <Typography fontSize={14} noWrap sx={trailingMetadataStyles}>
            {getFormattedDate(photo.meta.foundAt)}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={photoMetadataRowStyles}
        >
          <Typography
            fontSize={11}
            noWrap
            sx={leadingMetadataStyles}
            title={primaryId}
          >
            {primaryId}
          </Typography>
          <Typography
            fontSize={11}
            noWrap
            sx={trailingMetadataStyles}
            title={photo.meta.evId}
          >
            {photo.meta.evId}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

interface FindGalleryProps {
  finds: FindResult[];
  loading: boolean;
  state: FindResultsState;
  enableSiteFilter?: boolean;
  enableGroupFilter?: boolean;
  showCopyFromMap?: boolean;
  presetOmittedKeys?: readonly FindFilterListKey[];
}

export function FindGallery({
  finds,
  loading,
  state,
  enableSiteFilter = true,
  enableGroupFilter = true,
  showCopyFromMap = true,
  presetOmittedKeys = EMPTY_FIND_FILTER_LIST_KEYS,
}: FindGalleryProps) {
  const intl = useIntl();
  const vocabulary = useVocabulary();
  const { search, setSearch, galleryPages, setGalleryPages } = state;
  const loadedCount = galleryPages * GALLERY_PAGE_SIZE;
  const filteredFinds = useMemo(
    () =>
      finds.filter((find) =>
        matchesSearch(find, search, [
          vocabulary('findType', find.type),
          vocabulary('material', find.material),
          vocabulary('era', find.era),
        ])
      ),
    [finds, search, vocabulary]
  );
  const photos = useMemo(
    () =>
      filteredFinds.slice(0, loadedCount).map((find) => ({
        src: getImageSrc(find),
        key: find.id,
        width: 1080,
        height: 800,
        meta: {
          id: find.id,
          evId: find.evId,
          amcrId: find.amcrId,
          foundAt: find.foundAt,
          type: vocabulary('findType', find.type),
          unidentified: intl.formatMessage({
            id: 'find.type.unidentified',
            defaultMessage: 'Unidentified',
          }),
        },
      })),
    [filteredFinds, intl, loadedCount, vocabulary]
  );

  const handleLoadMore = () => setGalleryPages(galleryPages + 1);
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };
  const handleClearSearch = () => setSearch('');

  if (loading) {
    return (
      <div>
        <FormattedMessage id="loading" defaultMessage="Loading…" />
      </div>
    );
  }

  return (
    <Stack gap={3} mb={3}>
      <Stack direction="row" gap={1} alignItems="center">
        <TextField
          size="small"
          placeholder={intl.formatMessage({
            id: 'action.search',
            defaultMessage: 'Search',
          })}
          value={search}
          onChange={handleSearchChange}
          sx={searchFieldStyles}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {search ? (
                  <ClearIcon cursor="pointer" onClick={handleClearSearch} />
                ) : (
                  <ClearIcon htmlColor="transparent" />
                )}
              </InputAdornment>
            ),
          }}
        />
        <FindListFilters
          state={state}
          enableSiteFilter={enableSiteFilter}
          enableGroupFilter={enableGroupFilter}
          showCopyFromMap={showCopyFromMap}
          presetOmittedKeys={presetOmittedKeys}
        />
      </Stack>
      <PhotoAlbum
        targetRowHeight={200}
        padding={20}
        spacing={20}
        layout="rows"
        photos={photos}
        rowConstraints={galleryRowConstraints}
        renderPhoto={renderPhoto}
      />
      <Stack direction="row" justifyContent="center">
        <Button
          variant="contained"
          disabled={loadedCount >= filteredFinds.length}
          type="button"
          onClick={handleLoadMore}
        >
          <FormattedMessage id="action.loadMore" defaultMessage="Load more" />
        </Button>
      </Stack>
    </Stack>
  );
}
